import { useMemo } from 'react';
import {
  Card, Table, TableBody, TableCell, TableFoot, TableFooterCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import EmptyState from './EmptyState';
import { t } from '../lib/i18n';
import type { StaffLineRow } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const fmtNum = new Intl.NumberFormat('es-MX');

interface PersonGroup {
  id: string;
  name: string;
  lines: StaffLineRow[];
  units: number;
  revenue: number;
  margin: number;
}

/**
 * v2.45 — renders per-staff sales grouped by product line: one card per
 * person, a row per line (the per-line subtotal), and a person total in the
 * footer. Different lines carry different commission rates, so the breakdown
 * is what payroll works from. margin column shown only when allowed.
 */
export default function StaffLinesTable({ rows, showMargin }: { rows: StaffLineRow[]; showMargin: boolean }) {
  const groups = useMemo<PersonGroup[]>(() => {
    const m = new Map<string, PersonGroup>();
    for (const r of rows) {
      let g = m.get(r.id);
      if (!g) {
        g = { id: r.id, name: r.name, lines: [], units: 0, revenue: 0, margin: 0 };
        m.set(r.id, g);
      }
      g.lines.push(r);
      g.units += r.units;
      g.revenue += r.revenue_mxn;
      g.margin += r.margin_mxn ?? 0;
    }
    // Each person's lines sorted by revenue desc; people by total revenue desc.
    const out = [...m.values()];
    out.forEach((g) => g.lines.sort((a, b) => b.revenue_mxn - a.revenue_mxn));
    out.sort((a, b) => b.revenue - a.revenue);
    return out;
  }, [rows]);

  if (rows.length === 0) return <EmptyState message={t.salespeople.byLine.empty} />;

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <Card key={g.id} className="p-0 overflow-hidden">
          <div className="flex items-baseline justify-between px-4 pt-3">
            <Title className="text-base">{g.name}</Title>
            <Text className="text-tremor-content text-xs">{g.lines.length} {t.salespeople.byLine.lineCount}</Text>
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.salespeople.byLine.cols.line}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.salespeople.byLine.cols.units}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.salespeople.byLine.cols.revenue}</TableHeaderCell>
                {showMargin && <TableHeaderCell className="text-right">{t.salespeople.byLine.cols.margin}</TableHeaderCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {g.lines.map((l) => (
                <TableRow key={l.line}>
                  <TableCell>{l.line}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum.format(l.units)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMxn.format(l.revenue_mxn)}</TableCell>
                  {showMargin && (
                    <TableCell className="text-right tabular-nums">{fmtMxn.format(l.margin_mxn ?? 0)}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            <TableFoot>
              <TableRow>
                <TableFooterCell>{t.salespeople.byLine.total}</TableFooterCell>
                <TableFooterCell className="text-right tabular-nums">{fmtNum.format(g.units)}</TableFooterCell>
                <TableFooterCell className="text-right tabular-nums">{fmtMxn.format(g.revenue)}</TableFooterCell>
                {showMargin && (
                  <TableFooterCell className="text-right tabular-nums">{fmtMxn.format(g.margin)}</TableFooterCell>
                )}
              </TableRow>
            </TableFoot>
          </Table>
        </Card>
      ))}
    </div>
  );
}
